import { CharacterData, getBrawlerData, createDefaultBrawlerData } from '../types/character';
import { BaseClassProvider } from './BaseClassProvider';
import { ClassSectionComponent, ClassCombatComponent } from '../utils/ClassComponentRegistry';

// Import Brawler components
import { ClassFeaturesPanel } from '../components/CharacterSheet/ClassFeatures';
import Stances from '../components/CharacterSheet/Stances';
import Focus from '../components/CombatTracker/Focus';

/**
 * Provider for Brawler class-specific functionality
 * Registers all Brawler-specific components and logic
 * Updated to use new ClassFeaturesPanel (Phase 1)
 */
export class BrawlerProvider extends BaseClassProvider {
  readonly className = 'brawler';

  readonly characterSheetSections: ClassSectionComponent[] = [
    this.createSection(ClassFeaturesPanel, 'Class Features', {
      priority: 'high',
      defaultExpanded: false,
      order: 50 // Place before experiences/inventory
    }),
    this.createSection(Stances, 'Stances', {
      priority: 'high',
      defaultExpanded: false,
      order: 51 // Place after class features
    })
  ];

  readonly combatTrackerComponents: ClassCombatComponent[] = [
    this.createCombatComponent(Focus, 10) // Place early in combat tracker
  ];

  /**
   * Validate that character has proper Brawler data structure
   */
  validateCharacter(character: CharacterData): boolean {
    // Check base class validation first
    if (!super.validateCharacter(character)) {
      return false;
    }

    // Check if character has classSpecificData and brawler data
    if (!character.classSpecificData?.brawler) {
      return false;
    }

    // Validate Brawler-specific data structure
    const brawlerData = character.classSpecificData.brawler;
    return (
      typeof brawlerData.comboDie === 'string' &&
      typeof brawlerData.activeStance === 'string' &&
      Array.isArray(brawlerData.martialStances) &&
      typeof brawlerData.focusTokens === 'number' &&
      typeof brawlerData.maxFocusTokens === 'number'
    );
  }

  /**
   * Initialize character with default Brawler data if missing
   */
  initializeCharacter(character: CharacterData): Partial<CharacterData> {
    // Only initialize if character doesn't have valid Brawler data
    if (!character.classSpecificData?.brawler) {
      return {
        classSpecificData: {
          ...character.classSpecificData,
          brawler: createDefaultBrawlerData()
        }
      };
    }

    return {};
  }
}